import api from '@/services/api';
import { inMgnlAuthor } from '@/helpers/AppHelpers';
import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { formatNombreEnMonetaire } from '@/helpers/StringHelpers';

const getDefaultState = () => {
  return {
    address: null,
    balance: 0,
    messages: {
      deposit: {
        title: '',
        details: '',
      },
      voucher: {
        title: '',
        details: '',
      },
      billPayment: {
        title: '',
        details: '',
      },
    },
    esPayment: {
      paymentRef: '',
      lqEmail: '',
      montantEsFormat: null,
      mantantEsBrut: null,
    },

    profileDetails: null,
    hasProfession: false,

    verificationDetails: {
      adresseAffichage: {
        numero: null,
        rue: null,
        appartement: null,
        ville: null,
        state: 'QC',
        codePostal: null,
        ligneAdresse: null,
        ligneAdresse2: null,
      },
      links: [],
      status: null,
      verification: {
        verificationType: null,
        status: null,
        nominativeData: {
          firstname: null,
          lastname: null,
          dob: null,
          address: {
            apartmentNumber: null,
            civicNumber: null,
            street: null,
            city: null,
            state: 'QC',
            postcode: null,
          },
          phone: null,
          profession: null,
        },
      },
    },
    lastUpdate: '',
  };
};

const state = getDefaultState();

const getters = {
  hasAnyStatus: (state) => () => {
    return state.profileDetails.status instanceof Array && state.profileDetails.status.length !== 0;
  },
  hasStatus: (state) => (status) => {
    return state.profileDetails.status.includes(status);
  },

  getLastUpdate: (state) => {
    return state.lastUpdate;
  },
};

const actions = {
  reset({ commit }) {
    commit('RESET');
  },
  setLastUpdate: ({ commit, state }, value) => {
    commit('SET_LASTUPDATE', value);
    return state.lastUpdate;
  },

  // Retourne profileDetails du store sinon du service
  async getProfileDetails({ state, commit }, { depositInfo, withdrawInfo, forceRefresh }) {
    // On retourne le contenu du store sauf pour des flags à traitement manuel
    if (
      !forceRefresh &&
      !inMgnlAuthor &&
      process.env.VUE_APP_IS_MOCK === 'false' &&
      state.profileDetails &&
      !state.profileDetails.status.includes(playerPaymentProfileStatus.UPLOAD_ID) &&
      ((!depositInfo && !withdrawInfo) ||
        (depositInfo &&
          state.profileDetails.depositInfo &&
          state.profileDetails.depositInfo.hasDepositLimit == true))
    ) {
      return state.profileDetails;
    }

    return api
      .call('players/profile', 'getCustomerProfile', 'GET', {
        params: {
          'deposit-info': depositInfo,
          'withdrawal-info': withdrawInfo,
        },
      })
      .then((response) => {
        const paymentProfile = response.data.playerPaymentProfile;
        commit('SET_PROFILE_DETAILS', paymentProfile);
        commit('SET_HAS_PROFESSION', paymentProfile.hasProfession);
        if (paymentProfile?.depositInfo?.esInfo) {
          commit('SET_ES_PAYMENT_REF', paymentProfile?.depositInfo?.esInfo?.paymentRef);
          commit('SET_LQ_EMAIL', paymentProfile?.depositInfo?.esInfo?.emailLqSendMoney);
          commit('SET_MONTANT_ES', paymentProfile?.depositInfo?.esInfo?.amount);
        }
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  },

  // Retourne adresse du profile du client
  async getAddress({ commit }) {
      
      return api
        .call('players/address', 'playersAddress')
        .then((response) => {
          commit('SET_ADDRESS', response.data.addressInfo);
          return response.data.addressInfo;
        })
        .catch((error) => {
          throw error;
        });
  },

  // Retourne l'id de Verification VerifiedMe
  // Retourne les infos de Verification VerifiedMe
  async getVerificationDetails({ state }) {
    return state.verificationDetails.verification;
  },
  // Retourne les infos de Verification VerifiedMe
  async getVerificationInfo({ state }) {
    return state.verificationDetails.verification.nominativeData;
  },
  // Retourne les infos de Verification VerifiedMe
  async getVerificationStatus({ state }) {
    return state.verificationDetails.verification.status;
  },
  async getHasProfession({ state }) {
    return state.hasProfession;
  },
};

const mutations = {
  RESET(state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState());
  },
  RESET_ADDRESS(state) {
    state.address = null;
  },
  RESET_PROFILE_DETAILS(state) {
    state.profileDetails = null;
  },
  RESET_MESSAGES(state) {
    const message = { title: '', details: '' };
    state.messages.deposit = message;
    state.messages.voucher = message;
    state.messages.billPayment = message;
  },
  // Reset la vérification VerifiedMe
  RESET_VERIF_DETAILS(state) {
    state.verificationDetails = {};
    state.verificationDetails.adresseAffichage = {};
    state.verificationDetails.links = [];
    state.verificationDetails.status = null;
    state.verificationDetails.sessionId = null;
    state.verificationDetails.verification = {};
  },
  // Reset infos de vérification retournées par VerifiedMe
  RESET_VERIF_INFO(state) {
    state.verificationDetails.adresseAffichage = {};
    state.verificationDetails.verification = {};
  },
  RESET_ES_PAYEMENT(state) {
    state.esPayment = {};
  },
  SET_PROFILE_DETAILS(state, profileDetails) {
    state.profileDetails = profileDetails;
  },
  SET_HAS_PROFESSION(state, hasProfession) {
    state.hasProfession = hasProfession;
  },
  SET_DEPOSIT_INFO(state, depositInfo) {
    if (state.profileDetails?.depositInfo) {
      state.profileDetails.depositInfo = depositInfo;
    }
  },
  SET_ES_PAYMENT_REF(state, paymentRef) {
    state.esPayment.paymentRef = paymentRef;
  },
  SET_LQ_EMAIL(state, lqEmail) {
    state.esPayment.lqEmail = lqEmail;
  },
  SET_MONTANT_ES(state, montant) {
    state.esPayment.montantEsFormat = formatNombreEnMonetaire(montant, 2);
    state.esPayment.mantantEsBrut = montant;
  },
  // Set Adresse Infos
  SET_ADDRESS(state, address) {
    // On peut recevoir une adresse vide du service
    state.address = address;
  },
  // Set de la propriété custom 'joueurException' pour les joueurs sans profession
  SET_JOUEUR_EXCEPTION(state, joueurException) {
    state.joueurException = joueurException;
  },

  SET_MESSAGE_DEPOT(state, value) {
    if (value.title) {
      state.messages.deposit.title = value.title;
    }
    if (value.details) {
      state.messages.deposit.details = value.details;
    }
  },

  SET_MESSAGE_VOUCHER(state, value) {
    if (value.title) {
      state.messages.voucher.title = value.title;
    }
    if (value.details) {
      state.messages.voucher.details = value.details;
    }
  },

  SET_MESSAGE_BILLPAYMENT(state, value) {
    if (value.title) {
      state.messages.billPayment.title = value.title;
    }
    if (value.details) {
      state.messages.billPayment.details = value.details;
    }
  },
  // Setters VerifiedMe Verification Player Infos
  // Set Verification Adresse pour affichage
  SET_VERIF_ADRESSE_AFFICHAGE(state, adresse) {
    state.verificationDetails.adresseAffichage = adresse;
  },
  // Set VerificationStatus
  SET_VERIF_STATUS(state, status) {
    state.verificationDetails.verification.status = status;
  },
  // Set Verification NominativeData
  SET_VERIF_USER_INFO(state, nominativeData) {
    state.verificationDetails.verification.nominativeData = nominativeData;
  },
  // Set les infos de Vérification
  SET_VERIFICATION(state, verification) {
    state.verificationDetails.verification = verification;
  },
  SET_ID_PROFESSION(state, value) {
    state.profession.idProfession = value;
  },
  SET_ID_SITUATION_PROFESSIONNELLE(state, value) {
    state.profession.idSituationProfessionnelle = value;
  },
  SET_ID_SECTEUR_ACTIVITE(state, value) {
    state.profession.idSecteur = value;
  },
  SET_LASTUPDATE: (state, value) => {
    state.lastUpdate = value;
  },
};
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
